import { HorizontalContainer, Wrapper } from '@american-autonomy/ag-ui';

import EntitySelectPillButton from 'components/EntitySelectPillButton';
import WithField from 'components/WithField';
import BoundaryMultiSelectPanelField from 'components/fields/BoundaryMultiSelectPanelField';

import useMultiSelectField from 'hooks/useMultiSelectField';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FieldValues, Props } from './types';

import styles from './styles.module.scss';

// Reads its selections from the form but does not write them: toggling a boundary that belongs to
// another field has to be confirmed first, so both handlers are handed up to the consumer.
const MultiSelectBoundaryField = <T extends FieldValues>(props: Props<T>) => {
    const {
        className = '',
        control,
        excludedValues,
        name,
        onRemove,
        onToggle,
        ref,
        siteId,
        testId = TEST_ID
    } = props;

    const { BOUNDARIES, BOUNDARIES_PLACEHOLDER } = useTranslation();

    const { containerRef, handleToggle, isPanelOpen, value } = useMultiSelectField<T>({
        control,
        name,
        ref
    });

    const panelOpenClass = isPanelOpen ? styles.panelOpen : '';
    const hasValue = value.length > 0;

    return (
        <Wrapper
            className={`${styles.wrapper} ${panelOpenClass} ${className}`}
            ref={containerRef}
            testId={testId}>
            <WithField label={BOUNDARIES}>
                <HorizontalContainer className={styles.pillsContainer}>
                    {hasValue ? (
                        value.map(boundary => (
                            <EntitySelectPillButton
                                hasValue={true}
                                key={boundary.value}
                                onClick={handleToggle}
                                onRemove={() => onRemove(boundary)}
                                text={boundary.label}
                            />
                        ))
                    ) : (
                        <EntitySelectPillButton
                            hasValue={false}
                            onClick={handleToggle}
                            text={BOUNDARIES_PLACEHOLDER}
                        />
                    )}
                </HorizontalContainer>
            </WithField>

            {isPanelOpen && (
                <BoundaryMultiSelectPanelField
                    excludedValues={excludedValues}
                    onToggle={onToggle}
                    siteId={siteId}
                    value={value}
                />
            )}
        </Wrapper>
    );
};

export default MultiSelectBoundaryField;
